import React, { useState } from 'react';
import { 
  UserPlus, Phone, Baby, ShieldCheck, 
  ChevronRight, ChevronLeft, X, Save, 
  HeartPulse, Activity, Smile, Clock, MapPin
} from 'lucide-react';

// ✅ CANAL DE COMUNICACIÓN EN TIEMPO REAL
const canalTV = new BroadcastChannel('iglekids_tv');

const RegistroInvitado = ({ alCerrar, alFinalizar }) => {
  const [paso, setPaso] = useState(1);
  const [datos, setDatos] = useState({
    nombreTutor: '',
    telefono: '',
    nombreNino: '',
    edad: '', 
    grupoAsignado: 'CUNA A',
    temperatura: '36.5',
    hallazgos: [],
    alergias: []
  });

  const catalogoHallazgos = [
    { id: 'raspado', label: 'RASPÓN', emoji: '🩹', color: 'bg-orange-50 text-orange-600 border-orange-200' },
    { id: 'picadura', label: 'PICADURA', emoji: '🦟', color: 'bg-red-50 text-red-600 border-red-200' },
    { id: 'tos', label: 'TOS', emoji: '😷', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    { id: 'escurrimiento', label: 'FLUJO NASAL', emoji: '🤧', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    { id: 'ojos', label: 'OJOS ROJOS', emoji: '👁️', color: 'bg-rose-50 text-rose-600 border-rose-200' },
    { id: 'animo', label: 'DECAÍDO', emoji: '😴', color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  ];

  const catalogoAlergias = [
    { id: 'mani', label: 'MANÍ', emoji: '🥜' },
    { id: 'lactosa', label: 'LÁCTEOS', emoji: '🥛' },
    { id: 'gluten', label: 'GLUTEN', emoji: '🌾' },
    { id: 'fresa', label: 'FRESAS', emoji: '🍓' },
    { id: 'polvo', label: 'POLVO', emoji: '🧹' }
  ];

  const toggleItem = (campo, id) => {
    setDatos(prev => ({
      ...prev,
      [campo]: prev[campo].includes(id) 
        ? prev[campo].filter(i => i !== id) 
        : [...prev[campo], id]
    }));
  };

  const manejarGuardado = () => {
    const nombreMayus = datos.nombreNino.toUpperCase();
    
    // 1. Notificar a la TV de Pasillo vía BroadcastChannel
    const datosBienvenida = {
      tipo: 'BIENVENIDA',
      nombre: nombreMayus,
      salon: datos.grupoAsignado,
      timestamp: Date.now()
    };
    canalTV.postMessage(datosBienvenida);
    localStorage.setItem('ultimo_registro_tv', JSON.stringify(datosBienvenida));

    // 2. Ejecutar callback para actualizar estado en el padre
    alFinalizar({
      ...datos,
      nombre: nombreMayus,
      id: "INV-" + Date.now(), 
      presente: true,
      estatus: 'en_salon', // Consistencia con Firebase
      salon: datos.grupoAsignado, 
      horaIngreso: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      tutores: [{ nombre: datos.nombreTutor.toUpperCase(), tel: datos.telefono }],
      ticket: Math.floor(1000 + Math.random() * 9000).toString() 
    });
  };

  return (
    <div className="max-w-4xl mx-auto animate-in zoom-in-95 duration-500 font-sans text-left pb-10">
      <div className="bg-white rounded-[4.5rem] shadow-2xl border-4 border-slate-50 overflow-hidden relative">
        
        {/* HEADER */}
        <div className="bg-[#0A2440] p-12 text-white flex justify-between items-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />
          <div className="flex items-center gap-6 relative z-10">
            <div className="bg-amber-500 p-4 rounded-[2rem] text-[#0A2440] shadow-xl rotate-3">
              <UserPlus size={40} strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <h3 className="text-4xl font-black italic tracking-tighter leading-none uppercase">Registro Invitado</h3>
              <p className="text-[10px] font-black uppercase tracking-[0.5em] text-amber-400 mt-3 opacity-80">Nueva Familia</p>
            </div>
          </div>
          <button onClick={alCerrar} className="p-4 bg-white/10 rounded-2xl hover:bg-rose-600 transition-all border border-white/5 relative z-10">
            <X size={28} />
          </button>
        </div>

        {/* PROGRESS BAR */}
        <div className="flex px-12 py-8 gap-4 bg-slate-50/50 border-b border-slate-100">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex-1 space-y-2">
              <div className={`h-3 w-full rounded-full transition-all duration-700 ${paso >= i ? 'bg-amber-500 shadow-lg' : 'bg-slate-200'}`} />
              <p className={`text-[8px] font-black uppercase tracking-widest text-center ${paso === i ? 'text-amber-600' : 'text-slate-300'}`}>
                {i === 1 ? 'Tutor' : i === 2 ? 'Explorador' : 'Confirmar'}
              </p>
            </div>
          ))}
        </div>

        <div className="p-12 lg:p-20">
          {paso === 1 && (
            <div className="space-y-10 animate-in slide-in-from-right-12">
              <div className="flex items-center gap-4 border-b-2 border-slate-50 pb-6">
                 <Smile size={32} className="text-amber-500" />
                 <h4 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter leading-none text-left">Contacto Responsable</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 ml-6 uppercase tracking-widest block text-left">Nombre del Padre/Tutor</label>
                  <input 
                    type="text" placeholder="CARLOS MARTÍNEZ"
                    className="w-full p-8 bg-slate-50 rounded-[2.5rem] border-4 border-transparent focus:border-indigo-500 font-black text-xl outline-none shadow-inner uppercase"
                    value={datos.nombreTutor} onChange={(e) => setDatos({...datos, nombreTutor: e.target.value})}
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 ml-6 uppercase tracking-widest block text-left">Teléfono de Emergencia</label>
                  <div className="relative">
                    <Phone className="absolute left-8 top-1/2 -translate-y-1/2 text-indigo-500" size={24} />
                    <input 
                      type="tel" placeholder="9931234567"
                      className="w-full pl-20 p-8 bg-slate-50 rounded-[2.5rem] border-4 border-transparent focus:border-indigo-500 font-black text-xl outline-none shadow-inner"
                      value={datos.telefono} onChange={(e) => setDatos({...datos, telefono: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {paso === 2 && (
            <div className="space-y-12 animate-in slide-in-from-right-12">
              <div className="flex items-center gap-4 border-b-2 border-slate-50 pb-6">
                 <Baby size={32} className="text-amber-500" />
                 <h4 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter leading-none text-left">Datos del Menor</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-1 space-y-3">
                  <label className="text-[10px] font-black text-slate-400 ml-6 uppercase tracking-widest block text-left">Nombre</label>
                  <input 
                    type="text" placeholder="MATEO"
                    className="w-full p-6 bg-slate-50 rounded-[2rem] border-4 border-transparent focus:border-indigo-500 font-black text-xl outline-none uppercase shadow-inner"
                    value={datos.nombreNino} onChange={(e) => setDatos({...datos, nombreNino: e.target.value})}
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 ml-6 uppercase tracking-widest block text-left">Edad</label>
                  <input 
                    type="text" placeholder="4 AÑOS"
                    className="w-full p-6 bg-slate-50 rounded-[2rem] border-4 border-transparent focus:border-indigo-500 font-black text-xl outline-none shadow-inner"
                    value={datos.edad} onChange={(e) => setDatos({...datos, edad: e.target.value})}
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 ml-6 uppercase tracking-widest block text-left">Asignar Salón</label>
                  <div className="relative">
                    <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 text-indigo-500" size={20} />
                    <select 
                      className="w-full pl-16 p-6 bg-slate-50 rounded-[2rem] border-none font-black text-lg outline-none cursor-pointer text-indigo-600 appearance-none"
                      value={datos.grupoAsignado} onChange={(e) => setDatos({...datos, grupoAsignado: e.target.value})}
                    >
                      <option value="CUNA A">CUNA A</option>
                      <option value="PÁRVULOS B">PÁRVULOS B</option>
                      <option value="PRIMARIA 1">PRIMARIA 1</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-12 pt-4">
                <div className="space-y-6">
                  <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest italic ml-6 text-left">Filtro Sanitario Visual</p>
                  <div className="grid grid-cols-3 gap-3 p-6 bg-slate-50 rounded-[3rem]">
                    {catalogoHallazgos.map(h => (
                      <button key={h.id} onClick={() => toggleItem('hallazgos', h.id)} className={`p-4 rounded-2xl border-4 transition-all flex flex-col items-center gap-2 ${datos.hallazgos.includes(h.id) ? `${h.color} border-current shadow-md scale-105` : 'bg-white border-transparent opacity-40'}`}>
                        <span className="text-3xl">{h.emoji}</span>
                        <span className="text-[8px] font-black uppercase text-center leading-none">{h.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest italic ml-6 text-left">Alertas de Alergia</p>
                  <div className="grid grid-cols-3 gap-3 p-6 bg-slate-50 rounded-[3rem]">
                    {catalogoAlergias.map(a => (
                      <button key={a.id} onClick={() => toggleItem('alergias', a.id)} className={`p-4 rounded-2xl border-4 transition-all flex flex-col items-center gap-2 ${datos.alergias.includes(a.id) ? 'bg-rose-100 border-rose-400 text-rose-700 shadow-md scale-105' : 'bg-white border-transparent opacity-40'}`}>
                        <span className="text-3xl">{a.emoji}</span>
                        <span className="text-[8px] font-black uppercase text-center leading-none">{a.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {paso === 3 && (
            <div className="py-10 text-center space-y-10 animate-in zoom-in-95">
              <div className="w-32 h-32 bg-emerald-500 text-white rounded-[4rem] flex items-center justify-center mx-auto shadow-2xl rotate-3 border-8 border-white animate-bounce">
                <ShieldCheck size={64} />
              </div>
              <div className="space-y-3">
                <h4 className="text-5xl font-black text-slate-900 italic uppercase tracking-tighter">¡Listo para entrar!</h4>
                <p className="text-lg font-bold text-slate-400 italic">Verifica los datos del explorador invitado.</p>
              </div>
              
              <div className="bg-[#0A2440] p-10 rounded-[4rem] text-left border-b-[12px] border-amber-500 shadow-2xl relative max-w-xl mx-auto">
                <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-4">Pase de Bienvenida</p>
                <div className="space-y-2">
                    <p className="text-5xl font-black text-white italic tracking-tight uppercase leading-none">{datos.nombreNino}</p>
                    <div className="flex items-center gap-4 text-white/50 pt-2 font-bold uppercase tracking-widest text-sm">
                       <p>{datos.grupoAsignado}</p>
                       <span className="w-2 h-2 bg-amber-500 rounded-full" />
                       <p>{datos.edad}</p>
                    </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-16 flex gap-6">
            {paso > 1 && (
              <button onClick={() => setPaso(paso - 1)} className="px-12 py-7 rounded-[2.5rem] font-black text-slate-400 hover:bg-slate-100 transition-all flex items-center gap-3 uppercase text-xs tracking-widest">
                <ChevronLeft size={24} /> Volver
              </button>
            )}
            <button 
              onClick={() => paso < 3 ? setPaso(paso + 1) : manejarGuardado()}
              className={`flex-1 py-8 rounded-[3rem] font-black text-xl uppercase tracking-[0.3em] shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-4 border-b-[10px] ${paso === 3 ? 'bg-emerald-600 border-emerald-800 text-white' : 'bg-amber-500 border-amber-700 text-[#0A2440]'}`}
            >
              {paso === 3 ? <><Save size={28}/> Registrar e Imprimir</> : <>Siguiente <ChevronRight size={28}/></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistroInvitado;